import { useEffect, useMemo, useRef, useState } from "react";
import {
  createOptionsChart,
  CrosshairMode,
  IChartApiBase,
  ISeriesApi,
  LineData,
  LineSeries,
} from "lightweight-charts";
import { getPriceChartOptions } from "./lwcDefaults";
import { pickSeriesColor } from "./chartColors";
import { observeChartResize } from "./observeChartResize";
import { createChartRefreshState } from "./chartRefreshState";

export interface ScatterCommentItem {
  label: string;
  value: string;
}

export interface ScatterPoint {
  x: number;
  y: number;
  commentItems?: ScatterCommentItem[];
}

export interface ScatterSeries {
  serieName: string;
  points: ScatterPoint[];
}

interface Props {
  series: ScatterSeries[];
  titleX?: string;
  titleY?: string;
  className?: string;
  heightClassName?: string;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function fractionDigits(value: number): number {
  if (!Number.isFinite(value) || Math.abs(value - Math.round(value)) < 1e-9) {
    return 0;
  }
  const text = String(value);
  if (text.includes("e") || text.includes("E")) return 6;
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : Math.min(6, text.length - dot - 1);
}

function maxFractionDigits(values: number[]): number {
  return values.reduce((max, value) => Math.max(max, fractionDigits(value)), 0);
}

function formatNumericTick(value: number, precision: number): string {
  if (!Number.isFinite(value)) return "";
  const digits = Math.max(0, precision);
  if (digits <= 0) {
    const rounded = Math.round(value);
    return rounded === 0 ? "0" : rounded.toLocaleString("en-US");
  }
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return "0";
  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatYTick(value: number): string {
  if (!Number.isFinite(value)) return "";
  if (Math.abs(value) < 1e-12) return "0";
  if (Math.abs(value) >= 1000) {
    return Math.round(value).toLocaleString("en-US");
  }
  return formatNumericTick(value, 4);
}

function dedupe(points: LineData<number>[]): LineData<number>[] {
  const sorted = [...points].sort((a, b) => a.time - b.time);
  const out: LineData<number>[] = [];
  for (const p of sorted) {
    const last = out[out.length - 1];
    if (last && last.time === p.time) out[out.length - 1] = p;
    else out.push(p);
  }
  return out;
}

export function ScatterLineChart({
  series,
  titleX,
  titleY,
  className,
  heightClassName = "h-full min-h-[320px]",
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApiBase<number> | null>(null);
  const seriesRefs = useRef<ISeriesApi<"Line", number>[]>([]);
  const seriesNamesRef = useRef<string[]>([]);
  const metaRef = useRef<Map<string, Map<number, ScatterCommentItem[]>>>(
    new Map()
  );
  const xPrecisionRef = useRef(4);
  const refreshStateRef = useRef(createChartRefreshState());
  const tooltipMoveRef = useRef<(param: any) => void>(() => {});
  const [hiddenLegendLabels, setHiddenLegendLabels] = useState<Set<string>>(
    () => new Set()
  );
  const hiddenLegendLabelsRef = useRef(hiddenLegendLabels);
  hiddenLegendLabelsRef.current = hiddenLegendLabels;

  const applyLegendVisibility = () => {
    const hidden = hiddenLegendLabelsRef.current;
    seriesRefs.current.forEach((s, idx) => {
      const name = seriesNamesRef.current[idx];
      if (!name) return;
      try {
        s.applyOptions({ visible: !hidden.has(name) });
      } catch {
        /* ignore */
      }
    });
  };

  const toggleLegendSeries = (label: string) => {
    setHiddenLegendLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    lines: string[];
    placeLeft: boolean;
    placeAbove: boolean;
  } | null>(null);

  const legend = useMemo(
    () =>
      (series || []).map((s, i) => ({
        label: s.serieName,
        color: pickSeriesColor(i),
      })),
    [series]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const refreshState = refreshStateRef.current;

    const formatX = (time: number) =>
      formatNumericTick(time, xPrecisionRef.current);

    const chart = createOptionsChart(
      el,
      getPriceChartOptions({
        localization: {
          precision: 6,
          timeFormatter: formatX,
          priceFormatter: formatYTick,
        } as any,
        timeScale: {
          borderVisible: true,
          ticksVisible: true,
          minimumHeight: 28,
          tickMarkMaxCharacterLength: 12,
        },
        leftPriceScale: {
          minimumWidth: 88,
        },
        crosshair: {
          mode: CrosshairMode.Magnet,
          vertLine: {
            visible: true,
            labelVisible: true,
            color: "#758696",
            labelBackgroundColor: "#4c525e",
          },
          horzLine: {
            visible: true,
            labelVisible: true,
            color: "#758696",
            labelBackgroundColor: "#4c525e",
          },
        },
      })
    );
    chartRef.current = chart;

    const onMove = (param: any) => {
      if (
        !param?.point ||
        param.point.x < 0 ||
        param.point.y < 0 ||
        !param.seriesData?.size
      ) {
        setTooltip(null);
        return;
      }

      const lines: string[] = [];
      seriesRefs.current.forEach((s, idx) => {
        const name = seriesNamesRef.current[idx] || `Series ${idx + 1}`;
        if (hiddenLegendLabelsRef.current.has(name)) return;
        const raw = param.seriesData.get(s) as
          | { time?: number; value?: number }
          | undefined;
        if (!raw || raw.time == null) return;
        const comments = metaRef.current.get(name)?.get(raw.time) || [];
        if (comments.length) {
          comments.forEach((c) => lines.push(`${c.label}: ${c.value}`));
        } else if (raw.value != null) {
          lines.push(`${name}: ${formatYTick(raw.value)}`);
        }
      });

      if (!lines.length) {
        setTooltip(null);
        return;
      }

      const plotWidth = el.clientWidth || 300;
      const plotHeight = el.clientHeight || 180;
      setTooltip({
        x: param.point.x,
        y: param.point.y,
        lines,
        placeLeft: param.point.x > plotWidth / 2,
        placeAbove: param.point.y > plotHeight / 2,
      });
    };

    tooltipMoveRef.current = onMove;
    const handleMove = (param: any) =>
      refreshState.onCrosshairMove(param, onMove);
    chart.subscribeCrosshairMove(handleMove);

    const stopObserving = observeChartResize(el, (width, height) => {
      if (width <= 0 || height <= 0) return;
      chart.applyOptions({ width, height });
    });

    return () => {
      stopObserving();
      chart.unsubscribeCrosshairMove(handleMove);
      refreshState.reset();
      chart.remove();
      chartRef.current = null;
      seriesRefs.current = [];
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    refreshStateRef.current.beginUpdate(chart);

    seriesRefs.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {
        /* ignore */
      }
    });
    seriesRefs.current = [];
    seriesNamesRef.current = [];
    metaRef.current = new Map();

    const xValues = (series || []).flatMap((serie) =>
      (serie.points || [])
        .map((p) => toNumber(p.x))
        .filter((value): value is number => value != null)
    );
    const precision = Math.min(8, Math.max(4, maxFractionDigits(xValues)));
    xPrecisionRef.current = precision;
    chart.applyOptions({
      localization: {
        precision,
        timeFormatter: (time: number) => formatNumericTick(time, precision),
        priceFormatter: formatYTick,
      } as any,
    });

    (series || []).forEach((serie, index) => {
      const color = pickSeriesColor(index);
      const line = chart.addSeries(LineSeries, {
        color,
        lineWidth: 2,
        pointMarkersVisible: true,
        priceLineVisible: false,
        lastValueVisible: false,
        title: "",
        priceScaleId: "left",
      });

      const commentMap = new Map<number, ScatterCommentItem[]>();
      const points: LineData<number>[] = (serie.points || [])
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
        .map((p) => {
          if (p.commentItems?.length) commentMap.set(p.x, p.commentItems);
          return { time: p.x, value: p.y };
        });

      line.setData(dedupe(points));
      metaRef.current.set(serie.serieName, commentMap);
      seriesRefs.current.push(line);
      seriesNamesRef.current.push(serie.serieName);
    });

    applyLegendVisibility();
    const times = Array.from(new Set(
      seriesRefs.current.flatMap((line) => line.data().map((point) => point.time))
    )).sort((a, b) => a - b);
    refreshStateRef.current.finishUpdate(
      chart, times, seriesRefs.current, tooltipMoveRef.current,
    );
  }, [series]);

  useEffect(() => {
    applyLegendVisibility();
  }, [hiddenLegendLabels]);

  return (
    <div
      dir="ltr"
      className={`relative w-full flex flex-col ${heightClassName} ${
        className || ""
      }`}
      style={{ direction: "ltr" }}
      onClick={(e) => e.stopPropagation()}
    >
      {legend.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-4 shrink-0 py-1 text-xs text-gray-600">
          {legend.map((item) => {
            const hidden = hiddenLegendLabels.has(item.label);
            return (
              <span
                key={item.label}
                role="button"
                tabIndex={0}
                title={hidden ? "نمایش نمودار" : "مخفی کردن نمودار"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLegendSeries(item.label);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleLegendSeries(item.label);
                  }
                }}
                className="inline-flex items-center gap-1 cursor-pointer select-none"
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor: item.color,
                    opacity: hidden ? 0.35 : 1,
                  }}
                />
                <span className={hidden ? "line-through opacity-50" : ""}>
                  {item.label}
                </span>
              </span>
            );
          })}
        </div>
      ) : null}
      <div className="flex flex-1 min-h-0 min-w-0">
        {titleY ? (
          <div className="flex items-center justify-center shrink-0 w-7">
            <span
              className="text-xs text-gray-600 whitespace-nowrap"
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
              }}
            >
              {titleY}
            </span>
          </div>
        ) : null}
        <div className="relative flex-1 min-h-0 min-w-0">
          <div
            ref={containerRef}
            className="absolute inset-0"
            style={{ direction: "ltr" }}
          />
          {tooltip ? (
            <div
              className="pointer-events-none absolute z-20 max-w-sm rounded border border-gray-200 bg-white px-2 py-1 text-xs shadow text-left text-gray-800"
              dir="ltr"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                transform: `translate(${
                  tooltip.placeLeft ? "calc(-100% - 8px)" : "12px"
                }, ${tooltip.placeAbove ? "calc(-100% - 8px)" : "12px"})`,
              }}
            >
              {tooltip.lines.map((line, i) => (
                <div key={`${i}-${line}`} className="leading-5 whitespace-nowrap">
                  {line}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      {titleX ? (
        <div className="shrink-0 py-1 text-center text-xs text-gray-600">
          {titleX}
        </div>
      ) : null}
    </div>
  );
}

export default ScatterLineChart;
